/**
 * **The prices an estimate is computed at** (`docs/plans/org-pricing.md` OP7): the organization's
 * own, as `GET /orgs/current/prices` answers them — negotiated where an operator gave it a price,
 * the published one otherwise —, over the catalogue compiled into the bundle (`pricing/catalog.ts`).
 *
 * The catalogue is what a screen shows **before the read answers and outside an organization**,
 * and for any key the answer does not carry: a platform billed by contract answers no price, and
 * its screens keep the figures they showed before. Every estimate of the app takes a
 * {@link PriceBook} rather than reading the catalogue's constants, so an organization whose
 * subtitles are offered is told so, and one whose storage is negotiated is shown its own rate.
 *
 * Pure: the read and its cache are `composables/usePrices.ts`'s.
 */
import type { OrgPriceKey, OrgPricesView, VideoStorageBasis } from '@/api/billing/prices'
import { addDays } from './fees'
import { CHANNEL_MONTHLY_CENTS, CUSTOM_DOMAIN_MONTHLY_CENTS, FREE_BANDWIDTH_GB, USAGE_SKUS } from './pricing/catalog'

/** One of the six published prices. */
export type PriceKey = OrgPriceKey

/** The six, in the order the server answers them and a price list shows them. */
export const PRICE_KEYS: readonly PriceKey[] = [
  'channels',
  'custom_domain',
  'subtitles',
  'file_storage',
  'video_storage',
  'bandwidth',
]

/** Micro-cents in a cent, the unit the server quotes every price in. */
export const MICROCENTS_PER_CENT = 1_000_000

/** The decimal gigabyte storage and delivery are priced in, and delivery's allowance counted in. */
export const BYTES_PER_GIGABYTE = 1_000_000_000

/**
 * **Whether a price — or an amount — is offered**: exactly zero, in whatever unit it is counted.
 * Decided on the figure itself, never on one rounded for display: 0,3 c/Go is a price, and a
 * figure too small to show its cents is not a gift (`docs/plans/org-pricing.md` OP5).
 */
export function isOffered(figure: number): boolean {
  return figure === 0
}

/**
 * **What one charge debits**, in whole cents: its total — a quantity at its rate, fractional where
 * the rate is (`KeyPrice.cents`) — rounded **up** to the next cent, as the server debits a
 * transcription, each translation, a month of a channel or of a custom domain, and a file's first
 * month of storage. The ceiling is the charge's, never the unit price's: three minutes at 3,5 c are
 * 11 c, not 12. The total is rounded to the micro-cent first — the unit a price is quoted in — so a
 * product computed on doubles (100 × 0,07 = 7,000000000000001) does not cost a cent more.
 */
export function debitedCents(exactCents: number): number {
  return Math.ceil(Math.round(exactCents * MICROCENTS_PER_CENT) / MICROCENTS_PER_CENT)
}

/** What one key changes to, and from when. */
export interface PriceChange {
  /** `YYYY-MM-DD`. */
  from: string
  /** Cents excluding tax per priced unit; `0` is offered. */
  cents: number
  negotiated: boolean
  /** Delivery's monthly allowance in bytes; `null` on every other key. */
  freeBytes: number | null
}

/** One key's price, as an estimate reads it. */
export interface KeyPrice {
  key: PriceKey
  /**
   * Cents excluding tax per priced unit — a month of a channel, a domain, a gigabyte or an hour
   * stored, a started minute of subtitles, a gigabyte delivered past the allowance. **Fractional**
   * where a negotiated price is not a whole cent (3,5 c/Go).
   */
  cents: number
  /** The organization's own price rather than the published one. */
  negotiated: boolean
  /** Priced at zero: the screens say « Offert ». */
  offered: boolean
  /**
   * The first day the price no longer applies, `YYYY-MM-DD`; `null` while it runs on. Shown to a
   * person through {@link lastDayInForce}.
   */
  until: string | null
  /** What applies from `until`; `null` when nothing is scheduled. */
  next: PriceChange | null
  /** Delivery's monthly allowance in bytes; `null` on every other key. */
  freeBytes: number | null
}

/** Every price an estimate needs, and what video storage is billed on. */
export interface PriceBook {
  /** These are the organization's figures, as the server answered them — not the catalogue's. */
  served: boolean
  prices: Record<PriceKey, KeyPrice>
  /** `source_bytes`: a video is billed by its sources' gigabytes at the file storage price. */
  videoStorageBasis: VideoStorageBasis
  /** The first day the basis no longer applies; `null` while it runs on. */
  basisUntil: string | null
  /** What video storage is billed on from `basisUntil`. */
  basisNext: { from: string; basis: VideoStorageBasis } | null
}

/** The catalogue's price of a usage key, in cents per priced unit; 0 when it declares none. */
function usageCents(key: 'subtitles' | 'file_storage' | 'video_storage' | 'bandwidth'): number {
  return USAGE_SKUS.find((sku) => sku.key === key)?.unitCents ?? 0
}

/** The catalogue's price of every key, compiled from the backend's. */
function catalogCents(key: PriceKey): number {
  switch (key) {
    case 'channels':
      return CHANNEL_MONTHLY_CENTS
    case 'custom_domain':
      return CUSTOM_DOMAIN_MONTHLY_CENTS
    default:
      return usageCents(key)
  }
}

function catalogPrice(key: PriceKey): KeyPrice {
  const cents = catalogCents(key)
  return {
    key,
    cents,
    negotiated: false,
    offered: isOffered(cents),
    until: null,
    next: null,
    freeBytes: key === 'bandwidth' ? FREE_BANDWIDTH_GB * BYTES_PER_GIGABYTE : null,
  }
}

function catalogPrices(): Record<PriceKey, KeyPrice> {
  return Object.fromEntries(PRICE_KEYS.map((key) => [key, catalogPrice(key)])) as Record<PriceKey, KeyPrice>
}

/** The compiled catalogue as a price book: the published prices, the hours. */
export const CATALOG_PRICES: PriceBook = Object.freeze({
  served: false,
  prices: Object.freeze(catalogPrices()),
  videoStorageBasis: 'hours',
  basisUntil: null,
  basisNext: null,
}) as PriceBook

/**
 * **The organization's prices over the catalogue**: every key the answer carries replaces the
 * catalogue's figure, and a key it does not carry — every key, on a platform billed by contract —
 * keeps it. No answer yet is the catalogue.
 */
export function priceBookOf(view: OrgPricesView | null | undefined): PriceBook {
  if (!view?.sells_credits) return CATALOG_PRICES
  const prices = catalogPrices()
  for (const entry of view.prices) {
    const cents = entry.unit_price_microcents / MICROCENTS_PER_CENT
    prices[entry.key] = {
      key: entry.key,
      cents,
      negotiated: entry.negotiated,
      offered: isOffered(cents),
      until: entry.until ?? null,
      next: entry.next
        ? {
            from: entry.next.from,
            cents: entry.next.unit_price_microcents / MICROCENTS_PER_CENT,
            negotiated: entry.next.negotiated,
            freeBytes: entry.next.free_bytes ?? null,
          }
        : null,
      freeBytes: entry.free_bytes ?? null,
    }
  }
  const basis = view.video_storage_basis
  return {
    served: true,
    prices,
    videoStorageBasis: basis?.basis ?? 'hours',
    basisUntil: basis?.until ?? null,
    basisNext: basis?.next ? { from: basis.next.from, basis: basis.next.basis } : null,
  }
}

/**
 * **The last day a price applies**, `YYYY-MM-DD`, out of the `until` the server answers — the first
 * day it no longer does, a window being `[from, until)` (`docs/plans/org-pricing.md` OP4). It is
 * what a person is shown as an end, in the console too: a window `[27 Oct, 31 Oct)` runs through
 * the 30th, and what follows applies from the 31st.
 */
export function lastDayInForce(until: string): string {
  return addDays(until, -1)
}

/**
 * **A month of a channel's or a custom domain's fee, as it is debited**: the organization's price
 * rounded up to the cent ({@link debitedCents}) — what a screen announces as charged and holds the
 * wallet against. The price itself, fractional where negotiated so, is the price list's.
 */
export function monthlyFeeCents(key: 'channels' | 'custom_domain', book: PriceBook = CATALOG_PRICES): number {
  return debitedCents(book.prices[key].cents)
}

/** Whether video storage is billed by the bytes of the videos' sources, at the file price. */
export function billsVideosByBytes(book: PriceBook): boolean {
  return book.videoStorageBasis === 'source_bytes'
}
